const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://affiliatebackend.kampuskonnect2.com/api';

// Helper function to handle API responses
const handleResponse = async (response) => {
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }
  
  return data;
};

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Set auth token in localStorage
const setAuthToken = (token) => {
  localStorage.setItem('authToken', token);
};

// Remove auth token from localStorage
const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

// API service object
const apiService = {
  // Authentication endpoints
  auth: {
    signup: async (formData) => {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        body: formData // FormData object for file upload
      });
      
      const result = await handleResponse(response);
      
      // Store token if signup successful
      if (result.success && result.data.token) {
        setAuthToken(result.data.token);
      }
      
      return result;
    },

    login: async (credentials) => {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      });
      
      const result = await handleResponse(response);
      
      // Store token if login successful
      if (result.success && result.data.token) {
        setAuthToken(result.data.token);
      }
      
      return result;
    },

    getProfile: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    logout: () => {
      removeAuthToken();
      // Clear any other stored user data if needed
      localStorage.removeItem('userData');
    }
  },

  // Dashboard endpoints
  dashboard: {
    getStats: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/dashboard/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    getAnalytics: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/dashboard/analytics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    recordClick: async (referralCode) => {
      const response = await fetch(`${API_BASE_URL}/dashboard/click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ referralCode })
      });
      
      return await handleResponse(response);
    }
  },

  // Payout endpoints
  payout: {
    submitRequest: async (payoutData) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/payout/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payoutData)
      });
      
      return await handleResponse(response);
    },

    getSummary: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/payout/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    getEarningsHistory: async (page = 1, limit = 10) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/payout/history/earnings?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    getPayoutHistory: async (page = 1, limit = 10) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/payout/history/payouts?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    },

    cancelRequest: async (requestId) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/payout/cancel/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      return await handleResponse(response);
    }
  },

  // Utility functions
  utils: {
    getAuthToken,
    setAuthToken,
    removeAuthToken,
    isAuthenticated: () => !!getAuthToken(),
    
    // Helper to generate shareable content
    generateShareContent: (referralLink, platform = 'generic') => {
      const baseMessage = `🚀 Join KampusKonnect affiliate program and start earning!`;
      const callToAction = `Use my referral link: ${referralLink}`;
      
      switch (platform) {
        case 'whatsapp':
          return `${baseMessage} ${callToAction}`;
        case 'telegram':
          return `${baseMessage}\n${callToAction}`;
        case 'twitter':
          return `${baseMessage} ${callToAction} #KampusKonnect #Affiliate`;
        case 'email':
          return {
            subject: 'Join KampusKonnect Affiliate Program',
            body: `Hi there!\n\nI wanted to share an amazing opportunity with you. ${baseMessage}\n\n${callToAction}\n\nBest regards`
          };
        default:
          return `${baseMessage} ${callToAction}`;
      }
    },

    // Format currency
    formatCurrency: (amount, currency = 'USD') => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    },

    // Format date
    formatDate: (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};

export default apiService;